import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { CustomerShare } from "./pages/CustomerShare";
import { Home } from "./pages/Home";
import { Directory, type DirectorySearch } from "./pages/Directory";
import { ProgramDetail } from "./pages/ProgramDetail";
import { DepartmentDetail } from "./pages/DepartmentDetail";
import { Problems } from "./pages/Problems";
import { ProblemPage, type ProblemSearch } from "./pages/ProblemPage";
import { SearchPage, type SearchPageSearch } from "./pages/SearchPage";
import { ProgramEditor } from "./pages/admin/ProgramEditor";
import { DepartmentsAdmin } from "./pages/admin/DepartmentsAdmin";
import { CategoriesAdmin } from "./pages/admin/CategoriesAdmin";
import { VerificationQueue } from "./pages/admin/VerificationQueue";
import { Users } from "./pages/admin/Users";
import { ProblemsAdmin } from "./pages/admin/ProblemsAdmin";
import { SynonymsAdmin } from "./pages/admin/SynonymsAdmin";
import { IntentRulesAdmin } from "./pages/admin/IntentRulesAdmin";
import { RulesAdmin } from "./pages/admin/RulesAdmin";
import { TemplatesAdmin } from "./pages/admin/TemplatesAdmin";
import { Reports } from "./pages/admin/Reports";
import { Cases } from "./pages/Cases";
import { NewCase } from "./pages/NewCase";
import { CaseWorkspace } from "./pages/CaseWorkspace";
import { FollowUps } from "./pages/FollowUps";
import { Worksheets } from "./pages/Worksheets";
import { adminPage } from "./admin/AdminOnly";
import { managerPage } from "./admin/ManagerOnly";

export const rootRoute = createRootRoute({ component: () => <Outlet /> });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: Login });
const shareRoute = createRoute({ getParentRoute: () => rootRoute, path: "/s/$token", component: CustomerShare });
export const appRoute = createRoute({ getParentRoute: () => rootRoute, id: "app", component: Layout });
const homeRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: Home });

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
const searchRoute = createRoute({
  getParentRoute: () => appRoute, path: "/search", component: SearchPage,
  validateSearch: (s: Record<string, unknown>): SearchPageSearch => ({
    q: str(s.q), state: str(s.state), category: str(s.category), department: str(s.department), jurisdiction: str(s.jurisdiction),
  }),
});
const directoryRoute = createRoute({
  getParentRoute: () => appRoute, path: "/directory", component: Directory,
  validateSearch: (s: Record<string, unknown>): DirectorySearch => ({
    category: str(s.category), department: str(s.department), state: str(s.state), jurisdiction: str(s.jurisdiction), q: str(s.q),
  }),
});
const programRoute = createRoute({
  getParentRoute: () => appRoute, path: "/directory/programs/$slug", component: ProgramDetail,
  validateSearch: (s: Record<string, unknown>): { state?: string } => ({ state: str(s.state) }),
});
const departmentRoute = createRoute({ getParentRoute: () => appRoute, path: "/directory/departments/$slug", component: DepartmentDetail });
const problemsRoute = createRoute({ getParentRoute: () => appRoute, path: "/problems", component: Problems });
const problemRoute = createRoute({
  getParentRoute: () => appRoute, path: "/problems/$slug", component: ProblemPage,
  validateSearch: (s: Record<string, unknown>): ProblemSearch => ({ state: str(s.state) }),
});
const casesRoute = createRoute({ getParentRoute: () => appRoute, path: "/cases", component: Cases });
const newCaseRoute = createRoute({ getParentRoute: () => appRoute, path: "/cases/new", component: NewCase });
const caseRoute = createRoute({ getParentRoute: () => appRoute, path: "/cases/$id", component: CaseWorkspace });
const followupsRoute = createRoute({ getParentRoute: () => appRoute, path: "/followups", component: FollowUps });
const worksheetsRoute = createRoute({ getParentRoute: () => appRoute, path: "/worksheets", component: Worksheets });
const programEditorRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/programs/$id", component: adminPage(ProgramEditor) });
const departmentsAdminRoute = createRoute({
  getParentRoute: () => appRoute, path: "/admin/departments", component: adminPage(DepartmentsAdmin),
  validateSearch: (s: Record<string, unknown>): { id?: string } => ({ id: str(s.id) }),
});
const categoriesAdminRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/categories", component: adminPage(CategoriesAdmin) });
const verificationRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/verification", component: adminPage(VerificationQueue) });
const problemsAdminRoute = createRoute({
  getParentRoute: () => appRoute, path: "/admin/problems", component: adminPage(ProblemsAdmin),
  validateSearch: (s: Record<string, unknown>): { id?: string } => ({ id: str(s.id) }),
});
const synonymsAdminRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/synonyms", component: adminPage(SynonymsAdmin) });
const intentRulesAdminRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/intent-rules", component: adminPage(IntentRulesAdmin) });
const rulesAdminRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/rules", component: adminPage(RulesAdmin) });
const templatesAdminRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/templates", component: adminPage(TemplatesAdmin) });
const usersRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/users", component: adminPage(Users) });
const reportsRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin/reports", component: managerPage(Reports) });

const routeTree = rootRoute.addChildren([loginRoute, shareRoute, appRoute.addChildren([homeRoute, casesRoute, newCaseRoute, caseRoute, followupsRoute, worksheetsRoute, searchRoute, directoryRoute, programRoute,
  departmentRoute, problemsRoute, problemRoute, programEditorRoute, departmentsAdminRoute, categoriesAdminRoute, verificationRoute,
  problemsAdminRoute, synonymsAdminRoute, intentRulesAdminRoute, rulesAdminRoute, templatesAdminRoute, usersRoute, reportsRoute])]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}
